import Papa from 'papaparse';

export const MAX_CSV_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_CSV_DATA_ROWS = 5000;

export type CsvReadFailure =
  | 'notCsv'
  | 'tooLarge'
  | 'empty'
  | 'noHeader'
  | 'noDataRows'
  | 'tooManyRows'
  | 'malformed';

export interface CsvDataRow {
  /** Record number in the file, counting the header as 1 (like a spreadsheet). */
  rowNumber: number;
  /** Always as many cells as there are headers. */
  cells: string[];
}

export type ReadCsvResult =
  | { ok: true; headers: string[]; rows: CsvDataRow[] }
  | { ok: false; reason: CsvReadFailure };

const BOM = '﻿';

function decode(bytes: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/**
 * papaparse's own guess needs more than 1.99 fields per line on average and the trailing
 * newline counts as a one-field line, so two-column files are not detected. The header line
 * decides instead: whichever of `,` and `;` appears more outside quotes (ties go to `,`).
 */
function detectDelimiter(text: string): ',' | ';' {
  let commas = 0;
  let semicolons = 0;
  let inQuotes = false;
  let seenContent = false;

  for (const char of text) {
    if (char === '"') inQuotes = !inQuotes;
    else if (!inQuotes && (char === '\n' || char === '\r')) {
      if (seenContent) break;
    } else if (!inQuotes) {
      if (char === ',') commas++;
      if (char === ';') semicolons++;
    }
    if (char.trim() !== '') seenContent = true;
  }

  return semicolons > commas ? ';' : ',';
}

function isBlank(record: string[]): boolean {
  return record.every((cell) => cell.trim() === '');
}

function fitToWidth(record: string[], width: number): string[] {
  return Array.from({ length: width }, (_, index) => record[index] ?? '');
}

export async function readCsvFile(file: File): Promise<ReadCsvResult> {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return { ok: false, reason: 'notCsv' };
  }
  if (file.size > MAX_CSV_FILE_BYTES) return { ok: false, reason: 'tooLarge' };
  if (file.size === 0) return { ok: false, reason: 'empty' };

  const text = decode(await file.arrayBuffer()).replace(
    new RegExp(`^${BOM}`),
    ''
  );

  const parsed = Papa.parse<string[]>(text, {
    delimiter: detectDelimiter(text),
    skipEmptyLines: false,
  });
  // A missing closing quote would swallow the rest of the file into one cell.
  if (parsed.errors.some((error) => error.code === 'MissingQuotes')) {
    return { ok: false, reason: 'malformed' };
  }

  // Blank records are dropped here (not by papaparse) to keep the original record numbers.
  const records = parsed.data
    .map((cells, index) => ({ rowNumber: index + 1, cells }))
    .filter((record) => !isBlank(record.cells));

  const [headerRecord, ...dataRecords] = records;
  if (!headerRecord) return { ok: false, reason: 'empty' };
  if (headerRecord.rowNumber !== 1) return { ok: false, reason: 'noHeader' };
  if (dataRecords.length === 0) return { ok: false, reason: 'noDataRows' };
  if (dataRecords.length > MAX_CSV_DATA_ROWS) {
    return { ok: false, reason: 'tooManyRows' };
  }

  const headers = headerRecord.cells.map((cell) => cell.trim());
  return {
    ok: true,
    headers,
    rows: dataRecords.map((record) => ({
      rowNumber: record.rowNumber,
      cells: fitToWidth(record.cells, headers.length),
    })),
  };
}
